import type { Entity, Persisted } from '@tunasong/schemas'
import { useEffect, useState } from 'react'
import { applyFrozenOrder } from './space-sort.js'

/** How long "Recent activity" waits for the counts before showing rows in their own `updatedAt` order */
export const metaWaitMs = 800

export interface StableSpaceOrderProps {
  /** The spaces in the current sort's order */
  sorted: Persisted<Entity>[]
  /** The sort the viewer chose; changing it re-sorts and re-freezes */
  order: string
  /** Recent activity waits (briefly) for the counts, which can change the order; Name does not */
  waitForMeta: boolean
  /** The spaces list itself has loaded */
  listLoaded: boolean
  /** Every counts request has answered, with success or failure */
  metaSettled: boolean
}

/**
 * Rows never move once the viewer has seen them (Joni, 2026-10-17: a row that jumps under the cursor is a
 * misclick, and the viewer loses where things were).
 * - Hold the skeleton until the list has loaded and either the counts are in or `metaWaitMs` has passed.
 * - Then freeze the order shown. Counts and times keep filling in, but positions do not change until the next
 *   mount or until the viewer picks another sort.
 *
 * @returns the rows to show, or undefined to keep the skeleton
 */
export const useStableSpaceOrder = (props: StableSpaceOrderProps): Persisted<Entity>[] | undefined => {
  const { sorted, order, waitForMeta, listLoaded, metaSettled } = props
  const [waited, setWaited] = useState(false)
  const [frozen, setFrozen] = useState<{ order: string; ids: string[] } | null>(null)

  useEffect(() => {
    if (!listLoaded || waited) {
      return
    }
    const timer = setTimeout(() => setWaited(true), metaWaitMs)
    return () => clearTimeout(timer)
  }, [listLoaded, waited])

  const frozenIds = frozen?.order === order ? frozen.ids : null
  const ready = frozenIds !== null || (listLoaded && (!waitForMeta || metaSettled || waited))

  // Freeze what was first shown. The render that first shows rows shows `sorted`, which is what gets frozen here.
  useEffect(() => {
    if (ready && frozenIds === null && sorted.length > 0) {
      setFrozen({ order, ids: sorted.map(space => space.id) })
    }
  }, [ready, frozenIds, order, sorted])

  if (!ready) {
    return undefined
  }
  return frozenIds ? applyFrozenOrder(sorted, frozenIds) : sorted
}
